import { Fragment, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { useModuleBEM, useUiStyles } from "@b2bportal/core-themes";
import {
  CoreUiComponents,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import * as RadixTooltip from "@radix-ui/react-tooltip";
import type { TooltipProps as RadixTooltipProps } from "@radix-ui/react-tooltip";
import clsx from "clsx";
import defaultStyles from "./Tooltip.module.scss";

export type TooltipType = RadixTooltipProps & {
  content: ReactNode;
  triggerProps?: ComponentPropsWithoutRef<typeof RadixTooltip.Trigger>;
  contentProps?: ComponentPropsWithoutRef<typeof RadixTooltip.Content>;
  portalProps?: ComponentPropsWithoutRef<typeof RadixTooltip.Portal>;
  disablePortal?: boolean;
} & PlatformComponentProps;

const COMPONENT_KEY = CoreUiComponents.Tooltip;

export const Tooltip = ({
  children,
  content,
  triggerProps,
  contentProps,
  portalProps,
  disablePortal = true,
  className,
  ...props
}: TooltipType) => {
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY, {
    portal: !disablePortal,
  });

  const Portal =
    disablePortal != null && disablePortal ? Fragment : RadixTooltip.Portal;

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root delayDuration={100} {...props}>
        <RadixTooltip.Trigger className={cn("trigger")} {...triggerProps}>
          {children}
        </RadixTooltip.Trigger>
        <Portal {...portalProps}>
          <RadixTooltip.Content
            className={clsx(block, className)}
            side="bottom"
            align="center"
            sideOffset={10}
            {...contentProps}
          >
            {content}
          </RadixTooltip.Content>
        </Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};
